import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { addVehicleBindQuery } from '@amfam/policy/add-vehicle/data-access';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService } from '@amfam/ui-kit';

import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';

@Component({
  selector: 'ds-vehicle-purchase-info',
  templateUrl: './vehicle-purchase-info.component.html',
  styleUrls: ['../add-vehicle-container/add-vehicle-container.component.scss'],
  standalone: false
})
export class VehiclePurchaseInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() cancelEvent = new EventEmitter<boolean>();
  @Output() errorEvent = new EventEmitter<boolean>();
  public vehicleRiskDescription$ = this.store.select(vehicleQuery.selectNewRiskDescription);
  public purchaseInfoForm: UntypedFormGroup;
  public newVehicleDiscountQuestion$: Observable<string>;
  isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  private stop$: Subject<void> = new Subject<void>();
  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private spinnerService: LoadingSpinnerService,
    private copyService: CopyService
  ) {}

  ngOnInit(): void {
    this.spinnerService.stop();
    this.buildForm();
    this.handleError();

    this.newVehicleDiscountQuestion$ = this.store.select(userQuery.selectUserStateCode).pipe(
      take(1),
      map(userState => {
        const isNewVehicleDiscountState = !!this.copyService
          .getCopy('policy.newVehicleDiscountStates')
          .find(state => state === userState);
        return isNewVehicleDiscountState
          ? this.copyService.getCopy('policy.newVehicle30DayDiscountQuestion')
          : this.copyService.getCopy('policy.newVehicle14DayDiscountQuestion');
      })
    );
  }

  ngAfterViewInit() {
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { pageAnalyticName: 'Newvehicle' }
      })
    );
  }

  submitPurchaseInfo() {
    this.spinnerService.start({
      blockActions: true
    });

    const isNewVehicleDiscountActive =
      this.purchaseInfoForm.controls['isNewVehicleDiscountActive'].value === 'yes';

    this.store.dispatch(
      VehicleInfoActions.setNewVehicleDiscount({
        isNewVehicleDiscountActive
      })
    );

    if (isNewVehicleDiscountActive) {
      this.store.dispatch(
        fromRouterActions.Go({
          path: ['policies/add-vehicle/agent-help']
        })
      );
    } else {
      this.store.dispatch(
        fromRouterActions.Go({
          path: ['policies/add-vehicle/primary-use-info']
        })
      );
    }
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  private buildForm() {
    this.purchaseInfoForm = this.formBuilder.group({
      isNewVehicleDiscountActive: ['', Validators.compose([Validators.required])]
    });
  }

  private handleError() {
    this.store
      .select(addVehicleBindQuery.selectVehicleANSNotificationError)
      .pipe(takeUntil(this.stop$))
      .subscribe(agentNotificationError => {
        if (agentNotificationError) {
          this.spinnerService.stop();
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['policies/add-vehicle/agent-help']
            })
          );
        } else {
          this.errorEvent.emit(false);
        }
      });
  }
}
